/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { Options } from '../options.interface';
import { writeWizbiiTokensInCookie } from '../utils';
import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { JwtTokens } from '@wizbii-utils/angular/jwt';
import {
  AdmiinRole,
  City,
  Country,
  CountryService,
  EmailPreference,
  EmailUnsubscribePayload,
  EmailUnsubscribeReason,
  IdentityCard,
  LocaleEnum,
  Service,
  Token,
  toIdentityCard,
} from '@wizbii/utils/models';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface AccountApiConfig {
  appId: string;
  baseUrl: string;
  locale: string;
  platform: string;
  cookieDomains: Record<string, string>;
  defaultCookieDomain?: string;
}

export interface SocialLogin {
  tokens: JwtTokens;
  userStatus: 'existing' | 'created' | 'associated';
}

export const ACCOUNT_API_CONFIG = new InjectionToken<AccountApiConfig>('ACCOUNT_API_CONFIG');

export interface SignInWithAppleData {
  user: { email: string; name: { firstName: string; lastName: string } };
  authorization: { code: string; id_token: string; state: string };
}

@Injectable()
export class AccountWebservice {
  readonly #httpClient = inject(HttpClient);
  readonly #cookieService = inject(CookieService);
  readonly accountApiConfig = inject(ACCOUNT_API_CONFIG);

  readonly #appId = this.accountApiConfig.appId;
  readonly #baseUrl = this.accountApiConfig.baseUrl;
  readonly #locale = this.accountApiConfig.locale;
  readonly #platform = this.accountApiConfig.platform;
  readonly #cookieDomains = this.accountApiConfig.cookieDomains;
  readonly #defaultCookieDomain = this.accountApiConfig.defaultCookieDomain;

  /**
   * Get Wizbii services (Drive, Fibii ...)
   * @deprecated
   */
  getServicesList(options?: Options): Observable<Service[]> {
    return this.#httpClient.get<Service[]>(`${this.#baseUrl}/v1/services/${this.#locale}`, options);
  }

  /**
   * Delete a Wizbii service (Drive, Fibii ...)
   * @param {string} appId appId
   * @param options
   */
  deleteService(appId: string, options?: Options): Observable<void> {
    return this.#httpClient.delete<void>(`${this.#baseUrl}/v1/services/delete/${appId}`, options);
  }

  /**
   * Delete from admiin a Wizbii service (Drive, Fibii ...)
   * @param {string} appId appId
   * @param {string} id user id
   * @param options
   */
  adminDeleteService(appId: string, id: string, options?: Options): Observable<void> {
    return this.#httpClient.delete<void>(`${this.#baseUrl}/v1/services/delete/${appId}/${id}`, options);
  }

  /**
   * Get an identity card by its ID
   * @param {string} identityCardId ID of the identity card
   * @param options
   */
  getIdentityCard(identityCardId: string, options?: Options): Observable<IdentityCard> {
    return this.#httpClient
      .get<IdentityCard>(`${this.#baseUrl}/v1/identity-card/${identityCardId}`, options)
      .pipe(map(toIdentityCard));
  }

  /**
   * Get email preferences
   * @param {string} userId ID of the user
   * @param options
   */
  getEmailPreferences(userId: string, options?: Options): Observable<EmailPreference> {
    return this.#httpClient.get<EmailPreference>(`${this.#baseUrl}/v2/email-preferences/${userId}`, options);
  }

  /**
   * Update email preferences
   * @param {string} userId ID of the user
   * @param preference
   * @param options
   */
  updateEmailPreferences(userId: string, preference: EmailPreference, options?: Options): Observable<EmailPreference> {
    return this.#httpClient.patch<EmailPreference>(
      `${this.#baseUrl}/v2/email-preferences/${userId}`,
      { ...preference, appId: this.#appId },
      options
    );
  }

  /**
   * Gets a list of unsubscribe reasons
   */
  getUnsubscribeReasonsV2(): Observable<EmailUnsubscribeReason[]> {
    return this.#httpClient.get<EmailUnsubscribeReason[]>(`${this.#baseUrl}/v2/unsubscribe-reasons`);
  }

  /**
   * Adds unsubscribe to email reasons
   * @param contactId
   * @param payload
   * @param options
   */
  unsubscribeReasonsV2(contactId: string, payload: EmailUnsubscribePayload, options?: Options): Observable<void> {
    return this.#httpClient.patch<void>(`${this.#baseUrl}/v2/unsubscribe-reasons/${contactId}`, payload, options);
  }

  /**
   * Get all the role
   */
  getRoles(options?: Options): Observable<AdmiinRole[]> {
    return this.#httpClient.get<AdmiinRole[]>(`${this.#baseUrl}/v1/roles`, options);
  }

  /**
   * Get roles of user
   * @param {string} userId ID of the user
   * @param options
   */
  getMemberRoles(userId: string, options?: Options): Observable<string[]> {
    return this.#httpClient.get<string[]>(`${this.#baseUrl}/v1/roles/${userId}`, options);
  }

  /**
   * Add role to user
   * @param {string} userId ID of the user
   * @param {{role:string}} role ID of the user
   * @param options
   */
  addRole(userId: string, role: { role: string }, options?: Options): Observable<void> {
    return this.#httpClient.post<void>(`${this.#baseUrl}/v1/roles/${userId}/add-role`, role, options);
  }

  /**
   * Remove role to user
   * @param {string} userId ID of the user
   * @param {{role:string}} role ID of the user
   * @param options
   */
  removeRole(userId: string, role: { role: string }, options?: Options): Observable<void> {
    return this.#httpClient.post<void>(`${this.#baseUrl}/v1/roles/${userId}/remove-role`, role, options);
  }

  /**
   *
   * @param {string} profileId id of the profie
   * @param reasons
   * @param message
   * @param {Object} [options] Angular Options for the request
   */
  deleteAccount(profileId: string, reasons: string[], message: string, options?: Options): Observable<any> {
    return this.#httpClient.post<any>(
      `${this.#baseUrl}/v1/account/${profileId}/delete/${this.#locale}`,
      { reasons, message },
      options
    );
  }
  /**
   * Recover an account
   * @param {string} recoveryToken token to recover the account
   * @param {string} appId app-id when the account must be recover
   * @param options
   */
  recoverAccount(recoveryToken: string, appId = 'jobs', options?: Options): Observable<Token> {
    return this.#httpClient.post<Token>(
      `${this.#baseUrl}/v1/account/recover`,
      {
        recoveryToken,
        appId,
      },
      options
    );
  }

  /**
   * Update an identity card
   * @param {IdentityCard} identityCard updated identity card
   * @param options
   */
  updateIdentityCard(identityCard: IdentityCard, options?: Options): Observable<any> {
    return this.#httpClient.put<IdentityCard>(
      `${this.#baseUrl}/v1/identity-card/${identityCard.id}`,
      identityCard,
      options
    );
  }

  /**
   * get personal data
   * @param {string[]} appIds array of appId of the data requested
   * @param {Object} [options] Angular Options for the request
   */
  getPersonalData(appIds: string[], options?: Options): Observable<any> {
    return this.#httpClient.post<any>(
      `${this.#baseUrl}/v1/personal-data/request-export-account`,
      {
        appIds,
        locale: this.#locale,
      },
      options
    );
  }

  /**
   * get authentification chain
   * @param {string} username username to sign in
   * @param {string} password password to sign in
   * @param {string} finalUrl url where you want to be redirected at the end
   * @param appId
   * @param {Object} [options] Angular Options for the request
   */
  signIn(
    username: string,
    password: string,
    finalUrl: string,
    appId: string,
    options?: Options
  ): Observable<JwtTokens> {
    return this.#httpClient
      .post<JwtTokens>(
        `${this.#baseUrl}/v1/login`,
        {
          username,
          password,
          finalUrl,
          locale: this.#locale,
        },
        options
      )
      .pipe(
        tap((tokens) => {
          this.#writeTokensInCookie(tokens, appId);
        })
      );
  }

  /**
   * get register chain
   * @param {Object} account the account to create
   * @param {string} finalUrl url where you want to be redirected at the end
   * @param {Object} [options] Angular Options for the request
   */
  signUp(
    account: {
      firstName: string;
      lastName: string;
      username: string;
      password: string;
      appId?: string;
      dateBirthday?: Date | string;
      location?: City;
      mobile?: string;
      isSponsored?: boolean;
      source?: string;
    },
    finalUrl: string,
    options?: Options
  ): Observable<JwtTokens> {
    return this.#httpClient
      .post<JwtTokens>(
        `${this.#baseUrl}/v1/account`,
        {
          ...account,
          locale: this.#locale,
          appId: account.appId ? account.appId : 'account',
          finalUrl,
        },
        options
      )
      .pipe(
        tap((tokens) => {
          this.#writeTokensInCookie(tokens, account.appId!);
        })
      );
  }

  /**
   *
   * @param {string} userId The user id of the people
   * @param {string} source The source to set
   * @param {Object} [options] Angular Options for the request
   * @returns
   */
  setSource(userId: string, source?: string, options?: Options): Observable<void> {
    return this.#httpClient.post<void>(`${this.#baseUrl}/v1/account/${userId}/source`, { source }, options);
  }

  /**
   * get authentification chain with login token
   * @param {string} loginToken the login token to sign with
   * @param appId
   * @param {Object} [options] Angular Options for the request
   */
  loginWithToken(loginToken: string, appId: string, options?: Options): Observable<JwtTokens> {
    return this.#httpClient.post<JwtTokens>(`${this.#baseUrl}/v1/login-by-token`, { loginToken }, { ...options }).pipe(
      tap((tokens) => {
        this.#writeTokensInCookie(tokens, appId);
      })
    );
  }

  /**
   * Get the final redirection from login
   * @param {string} accessToken return from facebook SDK
   * @param {string} appId appId
   * @param {string} source the dource
   * @param {Object} [options] Angular Options for the request
   */
  facebookConnect(accessToken: string, appId: string, source?: string, options?: Options): Observable<SocialLogin> {
    return this.#httpClient
      .post<SocialLogin>(
        `${this.#baseUrl}/v1/login-by-facebook`,
        {
          socialAccount: {
            accessToken,
            locale: this.#locale,
            appId,
            source,
          },
        },
        options
      )
      .pipe(
        tap((socialLogin) => {
          this.#writeTokensInCookie(socialLogin.tokens, appId);
        })
      );
  }

  /**
   * Get the final redirection from login
   * @param {string} authCode return from google connect SDK
   * @param {string} appId appId
   * @param {string} source the dource
   * @param {Object} [options] Angular Options for the request
   */
  googleConnect(authCode: string, appId: string, source?: string, options?: Options): Observable<SocialLogin> {
    return this.#httpClient
      .post<SocialLogin>(
        `${this.#baseUrl}/v1/login-by-google`,
        {
          socialAccount: {
            authCode,
            locale: this.#locale,
            appId,
            source,
          },
        },
        options
      )
      .pipe(
        tap((socialLogin) => {
          this.#writeTokensInCookie(socialLogin.tokens, appId);
        })
      );
  }

  /**
   * Get the final redirection from login
   * @param {SignInWithAppleData} appleData
   * @param {string} appId appId initiating the request
   * @param {string} source the dource
   * @param {Object} [options] Angular Options for the request
   */
  appleConnect(
    appleData: SignInWithAppleData,
    appId: string,
    source?: string,
    options?: Options
  ): Observable<SocialLogin> {
    return this.#httpClient
      .post<SocialLogin>(
        `${this.#baseUrl}/v1/login-by-apple`,
        {
          socialAccount: {
            ...appleData,
            locale: this.#locale,
            appId,
            source,
          },
        },
        options
      )
      .pipe(
        tap((socialLogin) => {
          this.#writeTokensInCookie(socialLogin.tokens, appId);
        })
      );
  }

  /**
   *
   * @param {string} locale The country locale
   * @param {Object} [options] Angular Options for the request
   */
  getServicesByCountry(locale: LocaleEnum, options?: Options, version?: 'v1' | 'v2'): Observable<Country> {
    return this.#httpClient.get<Country>(`${this.#baseUrl}/${version ?? 'v1'}/country/${locale}`, options);
  }

  /**
   *
   * @param {string} service The service to get
   * @param {Object} [options] Angular Options for the request
   */
  getServiceByCountry(service: string, options?: Options): Observable<CountryService> {
    return this.#httpClient.get<CountryService>(
      `${this.#baseUrl}/v1/country/${this.#locale}/services/${service}`,
      options
    );
  }

  #writeTokensInCookie(tokens: JwtTokens, appId: string): void {
    writeWizbiiTokensInCookie(this.#cookieService, tokens, {
      cookieDomains: this.#cookieDomains,
      locale: this.#locale,
      defaultCookieDomain: this.#defaultCookieDomain,
      platform: this.#platform,
      appId,
    });
  }
}
